import moment from 'moment';
import * as api from './api';

/**
 * Convert object to querystring
 *
 * @param obj
 */
const serializeObject = obj => {
  if (Object.keys(obj).length === 0) return '';

  const str = [];
  Object.entries(obj).forEach(([key, value]) => {
    str.push(`${key}=${value}`);
  });
  return `?${str.join('&')}`;
};

const renameKeys = (obj, newKeys) => {
  const keyValues = Object.keys(obj).map(key => {
    const newKey = newKeys[key] || key;
    return { [newKey]: obj[key] };
  });
  return Object.assign({}, ...keyValues);
};

export const fetchForm = id => api.get(`inventory/form/${id}?grouped=true`);
export const fetchSpaces = unitId => api.get(`organizations/${unitId}/spaces/`);
export const fetchOrder = id => api.get(`orders/${id}`);

export const postOrder = orderData => api.post('/orders/', orderData);

export const postOrderFormless = orderData => api.post('orders/', orderData);

export const updateOrderFormless = (orderId, orderData) =>
  api.put(`orders/${orderId}/fields`, orderData);

export const getBottles = () => api.get('data/maintenance/bottles/');

export const getTransportTypes = () =>
  api.get('data/transport/transporttypes/');
export const getTransportUnits = () =>
  api.get('data/transport/transportunits/');

export const getSystems = async () => {
  const data = await api.get('data/ictsupport/systems/');
  return data.map(system => renameKeys(system, { name: 'label', id: 'value' }));
};
export const getFavouriteSystems = async () => {
  const data = await api.get('data/ictsupport/systems/search?favourites=true');
  return data.map(system => renameKeys(system, { name: 'text', id: 'value' }));
};

export const fetchFormModels = () => api.get('inventory/form/');

export const getTimeInfo = (formId, data = {}) =>
  api.get(`inventory/form/${formId}/timeinfo${serializeObject(data)}`);

export const getPatientData = patientId =>
  api.get(`data/patientdata/patient/${patientId} `);

export const toggleEmailNotification = isEnabled =>
  api.put('users/me/emailNotifications', isEnabled);

export const fetchTransferOrganizations = formId => {
  const queryParameters = `&forForm=${formId}`;
  return api.get(`organizations/?type=SERVICE_CONSUMER${queryParameters}`);
};

export const fetchHours = (formId, serviceParam) =>
  api.get(`inventory/form/${formId}/open/${serviceParam}`);

export const fetchTasks = ({
  categories,
  units,
  costCenters,
  statuses,
  criteria,
  startDate,
  endDate,
  myOrders,
  orderId,
  pageOffset,
}) => {
  const from = moment(startDate).toISOString();
  const to = moment(endDate).toISOString();

  // Default size seems to be 20. We'll take whatever the server gives us.
  let query = `?scope=SCOPE_CONSUMER_UNIT&page=${pageOffset}&from=${from}&to=${to}&categories=${categories.join(
    ','
  )}&statuses=${statuses}&criteria=${criteria}`;

  if (units?.length) query += `&units=${units.join(',')}`;
  if (costCenters?.length) query += `&costCenters=${costCenters.join(',')}`;

  // Optional params
  if (myOrders) query += `&myOrders=true`;
  if (orderId) query += `&orderId=${orderId}`;

  return api.get(`orders/${query}`);
};

export const fetchOrdersByStatuses = (status, startDate, endDate, onlyOwn) => {
  const scope = onlyOwn ? 'SCOPE_MY_ORDERS' : 'SCOPE_CONSUMER_UNIT';
  const from = moment(startDate).toISOString();
  const to = moment(endDate).toISOString();

  return api.get(
    `orders/?statuses=${status}&scope=${scope}&from=${from}&to=${to}`
  );
};
export const cancelOrder = (id, cancelReason = '') => {
  const orderData = { id: `${id}`, status: 'CANCELED', cancelReason };
  return api.put(`orders/${id}/status`, orderData);
};

export const completeOrder = id => {
  const orderData = { id: `${id}`, status: 'COMPLETED' };
  return api.put(`orders/${id}/status`, orderData);
};

export const editOrder = (orderData, orderId) =>
  api.put(`orders/${orderId}/items/`, orderData);

export const approve = id => {
  const orderData = { id: `${id}`, status: 'APPROVED' };
  return api.put(`orders/${id}/status`, orderData);
};

export const saveNickNameToSpace = (organizationId, space, name) => {
  const data = {
    hospital: `${space.hospital}`,
    building: `${space.building}`,
    office: `${space.office}`,
    floor: `${space.floor}`,
    room: `${space.room}`,
    nickname: `${name}`,
  };

  return api.put(`organizations/${organizationId}/spaces/`, data);
};

export const addComment = (orderId, message) =>
  api.post(`orders/${orderId}/comments/`, {
    comment: message,
  });
export const fetchNavigationItems = () => api.get('navigation/all');
export const fetchProfileData = () => api.get('users/me');
export const fetchNotifications = () => api.get('users/me/notifications');
export const fetchInventory = () => api.get('inventory/');
export const logOutQuery = () => api.del('users/logout');
export const fetchMunicipalities = () =>
  api.get('organizations/municipalities/');
export const fetchOffices = () => api.get('organizations/offices/');
export const fetchOrganizationData = (q = false) =>
  api.get(`organizations/?type=SERVICE_CONSUMER&sameResponsibility=${q}`);
export const getOrdersByField = (formId, fieldName, value, statuses) =>
  api.get(
    `inventory/form/${formId}/orders/?field=${fieldName}&value=${value}${
      statuses ? `&statuses=${statuses}` : ''
    }`
  );

export const fetchScheduleOptions = formId =>
  api.get(`inventory/form/${formId}/scheduleoptions`);

/**
 * NOTE:
 * The organizationId should be given as body content
 * but for some reason the server gets it from the query string.
 *
 * However, there has to be some content so that the cordova fetch plugin
 * does not fail with empty POST body.
 */
export const addOrganizationToUser = organizationId =>
  api.post(`users/me/organization?organizationId=${organizationId}`, {
    foo: 'bar',
  });

export const putFollowedOrganizations = orgs =>
  api.put(`users/me/followedOrganizations`, orgs);

export const fetchDeliveryAddresses = id =>
  api.get(`data/instruments/pickup-address/search?costcenter=${id}`);

export const getCostCenters = () => api.get('costcentres');
export const searchCostCenters = query =>
  api.get(`costcentres/search?page=0&size=15&search=${query}`);

export const uploadPhoto = data => api.post('pictures', data);
export const putOwnPhoneNumber = data => api.put('users/me/phone', data);

export const getPatientRooms = organizationId =>
  api.get(`organizations/${organizationId}/spaces/?roomType=PATIENT_ROOM`);
export const getOperatingRooms = organizationId =>
  api.get(`organizations/${organizationId}/spaces/?roomType=OPERATING_ROOM`);
export const getPatientAndOperatingRooms = organizationId =>
  api.get(
    `organizations/${organizationId}/spaces/?roomType=PATIENT_ROOM,OPERATING_ROOM`
  );
export const getExamRooms = organizationId =>
  api.get(`organizations/${organizationId}/spaces/?roomType=EXAMINATION_ROOM`);

export const getBuildings = officeId =>
  api.get(`spaces/buildings/find?officeId=${officeId}`);
